import React, { Suspense, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Database, Settings, BarChart3, Globe, Shield, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';

interface StepCardProps {
  step: {
    title: string;
    text: string;
  };
  index: number;
  position: {
    top: number;
    align: 'left' | 'right';
  };
}

/**
 * Dynamic PIM Workflow Visualization Component
 */
function DynamicPIMWorkflow() {
  const { getLandingInfographicSteps } = useMarketingTranslations();
  const steps = getLandingInfographicSteps();

  // Define workflow steps with better spacing and positioning
  const workflowSteps = [
    {
      icon: Database,
      title: steps[0]?.title || 'Centraliza Datos',
      description:
        steps[0]?.text || 'Importa toda tu información de productos en un hub centralizado',
      position: { x: 20, y: 13 },
      color: 'from-blue-500 to-cyan-500',
      delay: 0.2,
    },
    {
      icon: Settings,
      title: steps[1]?.title || 'Estructura y Enriquece',
      description:
        steps[1]?.text ||
        'Define familias, atributos y reglas. Asegura que cada producto esté listo para el mercado',
      position: { x: -10, y: 48 },
      color: 'from-emerald-500 to-teal-500',
      delay: 0.4,
    },
    {
      icon: Shield,
      title: steps[2]?.title || 'Control de Calidad',
      description: steps[2]?.text || 'Validación automática y control de calidad de datos',
      position: { x: 45, y: 48 },
      color: 'from-purple-500 to-violet-500',
      delay: 0.6,
    },
    {
      icon: Globe,
      title: steps[3]?.title || 'Localiza y Ajusta Precios',
      description:
        steps[3]?.text || 'Personaliza idioma, moneda o canal. Listo para B2B, DTC y marketplaces',
      position: { x: -10, y: 83 },
      color: 'from-orange-500 to-red-500',
      delay: 0.8,
    },
    {
      icon: BarChart3,
      title: steps[4]?.title || 'Colabora y Revisa',
      description:
        steps[4]?.text ||
        'Asigna roles, deja comentarios, rastrea cambios. Mantén equipos alineados',
      position: { x: 45, y: 83 },
      color: 'from-pink-500 to-rose-500',
      delay: 1.0,
    },
  ];

  // Connection paths between steps
  const connections = [
    { from: 0, to: 1, path: 'M 50 20 Q 32 30 15 55' },
    { from: 0, to: 2, path: 'M 50 20 Q 68 30 85 55' },
    { from: 1, to: 3, path: 'M 15 55 Q 15 70 15 95' },
    { from: 2, to: 4, path: 'M 85 55 Q 85 70 85 95' },
    { from: 3, to: 4, path: 'M 25 95 Q 50 85 75 95' },
  ];

  return (
    <div className="relative flex size-full min-h-[600px] items-center justify-center">
      {/* Subtle Central Flow */}
      <motion.div
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2, delay: 0.5 }}
      >
        <div className="bg-gradient-radial via-purple-500/3 size-48 rounded-full from-blue-500/5 to-transparent" />
      </motion.div>

      {/* Simplified Connection Lines */}
      <motion.svg
        className="pointer-events-none absolute inset-0 size-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
      >
        {connections.slice(0, 3).map((connection, index) => (
          <motion.path
            key={index}
            d={connection.path}
            fill="none"
            stroke="url(#gradient)"
            strokeWidth="1"
            strokeDasharray="3,6"
            className="opacity-30"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.3 }}
            transition={{
              duration: 2,
              delay: 2 + index * 0.3,
              ease: 'easeInOut',
            }}
          />
        ))}
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.2" />
          </linearGradient>
        </defs>
      </motion.svg>

      {/* Workflow Step Cards */}
      {workflowSteps.map((step, index) => {
        const IconComponent = step.icon;
        return (
          <motion.div
            key={index}
            className="group absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            style={{
              left: `${step.position.x}%`,
              top: `${step.position.y}%`,
            }}
            initial={{ opacity: 0, scale: 0, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: step.delay,
              type: 'spring',
              bounce: 0.4,
            }}
            whileHover={{
              scale: 1.1,
              y: -10,
              transition: { duration: 0.3 },
            }}
          >
            {/* Main Card */}
            <motion.div
              className="relative w-[330px] rounded-2xl 
              border border-slate-600/50 bg-slate-800/90 p-5 shadow-2xl backdrop-blur-md 
              transition-all duration-300 group-hover:border-primary-400/50"
              whileHover={{
                boxShadow: '0 25px 50px -12px rgba(59, 130, 246, 0.25)',
              }}
            >
              {/* Glowing Background */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${step.color} rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-10`}
                initial={false}
              />

              {/* Icon */}
              <motion.div
                className={`size-14 rounded-xl bg-gradient-to-br ${step.color} relative z-10 mb-3 flex items-center justify-center`}
                whileHover={{ rotate: 30 }}
                transition={{ duration: 0.6 }}
              >
                <IconComponent className="size-7 text-white" />

                {/* Icon Glow */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${step.color} rounded-xl opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-60`}
                  initial={false}
                />
              </motion.div>

              {/* Content */}
              <div className="relative z-10">
                <motion.h3
                  className="mb-2 text-lg font-bold text-white transition-colors group-hover:text-primary-300"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: step.delay + 0.2 }}
                >
                  {step.title}
                </motion.h3>

                <motion.p
                  className="flex min-h-72 items-start text-sm leading-relaxed text-slate-300 transition-colors group-hover:text-slate-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: step.delay + 0.3 }}
                >
                  {step.description}
                </motion.p>
              </div>

              {/* Floating Number Badge */}
              <motion.div
                className={`absolute -right-2 -top-2 size-8 bg-gradient-to-br ${step.color} flex items-center justify-center rounded-full text-sm font-bold text-white shadow-lg`}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  duration: 0.5,
                  delay: step.delay + 0.4,
                  type: 'spring',
                  bounce: 0.6,
                }}
                whileHover={{ scale: 1.2 }}
              >
                {index + 1}
              </motion.div>

              {/* Animated Corner Accents */}
              <motion.div
                className="absolute left-0 top-0 size-3 rounded-tl-lg border-l-2 border-t-2 border-primary-400/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                initial={false}
              />
              <motion.div
                className="absolute bottom-0 right-0 size-3 rounded-br-lg border-b-2 border-r-2 border-primary-400/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                initial={false}
              />
            </motion.div>
          </motion.div>
        );
      })}

      {/* Floating Data Points */}
      {[...Array(4)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute size-2 rounded-full bg-primary-400/40"
          style={{
            left: `${20 + Math.random() * 60}%`,
            top: `${20 + Math.random() * 60}%`,
          }}
          animate={{
            x: [0, Math.random() * 40 - 20, 0],
            y: [0, Math.random() * 40 - 20, 0],
            opacity: [0.2, 0.8, 0.2],
            scale: [0.5, 1.2, 0.5],
          }}
          transition={{
            duration: 4 + Math.random() * 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: Math.random() * 2,
          }}
        />
      ))}

      {/* Background Glow Effects */}
      <motion.div
        className="absolute left-1/4 top-1/4 size-64 rounded-full bg-primary-500/5 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <motion.div
        className="absolute bottom-1/4 right-1/4 size-48 rounded-full bg-purple-500/5 blur-2xl"
        animate={{
          scale: [1, 1.4, 1],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2,
        }}
      />
    </div>
  );
}

/**
 * Individual animated step card overlay - positioned absolutely over infographic
 */
function StepCard({ step, index, position }: StepCardProps) {
  const { align } = position;

  return (
    <motion.div
      className={`absolute z-20 max-w-[210px] text-sm leading-tight ${
        align === 'left' ? 'left-[23.1%]' : 'left-[43.8%]'
      }`}
      style={{
        top: `${position.top}px`,
      }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{
        delay: index * 0.12 + 0.2,
        duration: 0.5,
        ease: 'easeOut',
      }}
      whileHover={{
        scale: 1.02,
        transition: { duration: 0.2 },
      }}
    >
      <div className="from-slate-800/98 to-slate-900/98 flex h-[120px] w-[260px] flex-col rounded-lg border border-primary-400/40 bg-gradient-to-br px-4 py-2 shadow-lg ring-1 ring-white/10 backdrop-blur-md">
        <div className="mb-1 flex shrink-0 items-center">
          <div className="mr-1.5 flex size-4 items-center justify-center rounded-full bg-gradient-to-r from-primary-500 to-primary-400 text-xs font-bold text-white shadow-md">
            {index + 1}
          </div>
          <h3 className="text-xs font-semibold leading-tight text-white">{step.title}</h3>
        </div>
        <p className="grow overflow-hidden text-xs leading-snug text-slate-300">{step.text}</p>
      </div>
    </motion.div>
  );
}

/**
 * Enterprise-grade split-screen hero with dynamic PIM workflow
 */
export function HeroSplitInfographic() {
  const { i18n } = useTranslation();
  const { getLandingHero, getLandingInfographicSteps } = useMarketingTranslations();

  const steps = getLandingInfographicSteps();

  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden border-b border-slate-700/50 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20 pt-0"
      aria-labelledby="hero-title"
    >
      {/* Enhanced Background Effects */}
      <motion.div
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(56,189,248,0.1),transparent_50%)]"
        animate={{
          background: [
            'radial-gradient(circle at 30% 20%, rgba(56,189,248,0.1), transparent 50%)',
            'radial-gradient(circle at 70% 80%, rgba(139,92,246,0.1), transparent 50%)',
            'radial-gradient(circle at 30% 20%, rgba(56,189,248,0.1), transparent 50%)',
          ],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Animated Grid Pattern */}
      <motion.div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.4'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3Ccircle cx='53' cy='53' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
        animate={{
          backgroundPosition: ['0px 0px', '60px 60px', '0px 0px'],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      <div className="container mx-auto px-6 pb-16 pt-8">
        <div className="grid min-h-[90vh] items-start gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left Column - Content */}
          <motion.div
            className="relative z-10 mt-10 space-y-8 self-start lg:ml-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            {/* Enhanced Badge */}
            <motion.div
              className="inline-block"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <motion.div
                className="relative inline-flex items-center overflow-hidden rounded-full border border-primary-500/30 bg-gradient-to-r from-primary-500/20 to-primary-600/20 px-4 py-2 backdrop-blur-sm"
                whileHover={{ scale: 1.05 }}
              >
                {/* Badge Glow Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-primary-600/10"
                  animate={{
                    x: [-100, 100],
                    opacity: [0, 0.5, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
                <span className="relative z-10 text-sm font-semibold tracking-wide text-primary-300">
                  {getLandingHero('badge')}
                </span>
              </motion.div>
            </motion.div>

            {/* Enhanced Headline */}
            <motion.h1
              id="hero-title"
              className="text-4xl font-bold leading-[1.1] text-white md:text-5xl xl:text-6xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.span
                className="bg-gradient-to-r from-white via-slate-100 to-slate-200 bg-clip-text text-transparent"
                animate={{
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  backgroundSize: '200% 200%',
                }}
              >
                {getLandingHero('title')}
              </motion.span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              className="max-w-2xl text-lg leading-relaxed text-slate-300 md:text-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {getLandingHero('subtitle')}
            </motion.p>

            {/* Enhanced CTA Buttons */}
            <motion.div
              className="flex flex-col gap-4 pt-4 sm:flex-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  asChild
                  size="lg"
                  className="group relative h-auto overflow-hidden bg-gradient-to-r from-primary-600 to-primary-500 px-8 py-4 text-lg font-semibold text-white shadow-2xl shadow-primary-500/25 transition-all duration-300 hover:from-primary-700 hover:to-primary-600 hover:shadow-primary-500/40"
                >
                  <Link to="/marketing/pricing" className="relative z-10 flex items-center gap-3">
                    {/* Button Shine Effect */}
                    <motion.div
                      className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      animate={{
                        x: [-100, 300],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    {getLandingHero('seePricing')}
                    <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="h-auto border-2 border-slate-600 bg-slate-800/50 px-8 py-4 text-lg font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-slate-500 hover:bg-slate-700/70"
                >
                  <Link to="/login">{getLandingHero('login')}</Link>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right Column - Dynamic PIM Workflow */}
          <div
            className="relative order-first 
          mt-16 flex hidden min-h-[600px] 
          flex-col items-center justify-center lg:order-last lg:flex lg:h-[calc(100vh-120px)]"
          >
            <DynamicPIMWorkflow />

            {/* Enhanced Ambient Glow Effect */}
            <motion.div
              className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-r from-primary-500/5 to-purple-500/5 blur-3xl"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </div>
        </div>

        {/* Enhanced Mobile Steps Section */}
        <div className="mx-auto mt-16 max-w-3xl lg:hidden">
          <motion.div
            className="grid gap-4 sm:grid-cols-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="group rounded-xl border border-primary-400/20 bg-gradient-to-br from-slate-800/60 to-slate-900/60 p-4 backdrop-blur-md"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{
                  scale: 1.02,
                  borderColor: 'rgba(59, 130, 246, 0.4)',
                }}
              >
                <div className="mb-2 flex items-center">
                  <motion.div
                    className="mr-3 flex size-8 items-center justify-center rounded-full bg-gradient-to-r from-primary-500 to-primary-400 text-sm font-bold text-white shadow-lg"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    {index + 1}
                  </motion.div>
                  <h3 className="text-sm font-semibold leading-tight text-white transition-colors group-hover:text-primary-300">
                    {step.title}
                  </h3>
                </div>
                <p className="text-xs leading-relaxed text-slate-300 transition-colors group-hover:text-slate-200">
                  {step.text}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Enhanced Bottom Fade */}
      <motion.div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-800/50 to-transparent"
        animate={{
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </section>
  );
}
